import * as React from 'react';
import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

import { actions } from 'react-redux-form';
import { Action, Dispatch } from 'redux';

import { AppState } from '..';
import { usersService } from '@services/UsersService';
import { ProfileViewModel } from '@models/Auth';
import FormNames from '@constants/FormNames.enum';

// We can use enums instead of type constants like in the example. This is a newer TS feature
export enum ProfileActionType {
  /********** Save **********/
  SaveProfileStart = 'SaveProfileStart',
  SaveProfileSuccess = 'SaveProfileSuccess',
  SaveProfileFailure = 'SaveProfileFailure',

  /********** Get **********/
  GetProfileStart = 'GetProfileStart',
  GetProfileSuccess = 'GetProfileSuccess',
  GetProfileFailure = 'GetProfileFailure'
}

// This is defining the action definition for certain actions on the page.
// There may be multiple types of these on a given page.
// All properties should be readonly.

/********** Save **********/
export interface SaveProfileStartAction extends Action {
  readonly type: ProfileActionType.SaveProfileStart;
}

export interface SaveProfileSuccessAction extends Action {
  readonly type: ProfileActionType.SaveProfileSuccess;
  readonly profileId: number;
}

export interface SaveProfileFailureAction extends Action {
  readonly type: ProfileActionType.SaveProfileFailure;
  readonly errorMessage: string;
}

/********** Get **********/
export interface GetProfileStartAction extends Action {
  readonly type: ProfileActionType.GetProfileStart;
}

export interface GetProfileSuccessAction extends Action {
  readonly type: ProfileActionType.GetProfileSuccess;
  readonly currentProfile: ProfileViewModel;
}

export interface GetProfileFailureAction extends Action {
  readonly type: ProfileActionType.GetProfileFailure;
  readonly errorMessage: string;
}

// Actions for each action type declared here.
// They should all return an action object.

/********** Save **********/
export const saveProfile = (model: ProfileViewModel) => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(saveProfileStart());
    try {
      await usersService.update(model);
      dispatch(saveProfileSuccess(model.id));
      if (model.id === 0) {
        dispatch(actions.reset(FormNames.ProfileForm));
      }
    } catch (e) {
      dispatch(saveProfileFailure(e.toString()));
    }
  };
};

export function saveProfileStart(): SaveProfileStartAction {
  return {
    type: ProfileActionType.SaveProfileStart
  };
}

export function saveProfileSuccess(profileId: number): SaveProfileSuccessAction {
  toast.success(
    <ToastComponent type={ToastType.Success} message={'Profile Saved Successfully'} />,
    ToastOptions.Success
  );

  return {
    type: ProfileActionType.SaveProfileSuccess,
    profileId: profileId
  };
}

export function saveProfileFailure(errorMessage: string): SaveProfileFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to save profile. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ProfileActionType.SaveProfileFailure,
    errorMessage: errorMessage
  };
}

/********** Get **********/
export const getProfile = () => {
  return async (dispatch: Dispatch<AppState>) => {
    dispatch(getProfileStart());
    try {
      var profileVM = await usersService.getCurrentUser();
      dispatch(getProfileSuccess(profileVM));
      dispatch(actions.change(FormNames.ProfileForm, profileVM));
    } catch (e) {
      dispatch(getProfileFailure(e.toString()));
    }
  };
};

export function getProfileStart(): GetProfileStartAction {
  return {
    type: ProfileActionType.GetProfileStart
  };
}

export function getProfileSuccess(profile: ProfileViewModel): GetProfileSuccessAction {
  return {
    type: ProfileActionType.GetProfileSuccess,
    currentProfile: profile
  };
}

export function getProfileFailure(errorMessage: string): GetProfileFailureAction {
  toast.error(
    <ToastComponent type={ToastType.Error} message={'Network error, unable to get profile. ' + errorMessage} />,
    ToastOptions.Error
  );

  return {
    type: ProfileActionType.GetProfileFailure,
    errorMessage: errorMessage
  };
}



// WEBPACK FOOTER //
// ./src/profile/profile.actions.tsx